import { ChevronDownIcon } from 'lucide-react'
import { href, Link } from 'react-router'
import { BROWSE_BY_CITY } from '../cities'
import { formatLocationSlug } from '../slug'
import { SPECIALTIES_BY_LABEL } from '../specialties'

// Fixed slices, one per rendered column. CSS `columns` was the obvious fit, but
// multicol balances column heights: expanding a city grows its column, so the
// browser re-flows cities across the column boundaries and every city after the
// open one jumps to a different column. Slicing up front pins each city to a
// column, so opening one only moves the cities below it in its own column.
//
// The trade-off is that the slice count is baked in, so this only works for
// breakpoints where the column count is COLUMN_COUNT or 1 — at 1 the grid stacks
// the slices in order, which is the same sequence as a single column. Adding an
// intermediate 2-column breakpoint would need a different scheme.
const COLUMN_COUNT = 3
const CITY_COLUMNS = Array.from({ length: COLUMN_COUNT }, (_, column) => {
  const perColumn = Math.ceil(BROWSE_BY_CITY.length / COLUMN_COUNT)
  return BROWSE_BY_CITY.slice(column * perColumn, (column + 1) * perColumn)
})

// We have no location-only pages, so a city can't be a link — it expands to the
// specialty pages instead. `<details>` keeps every link in the server HTML
// (closed children are only visually hidden by the UA stylesheet), so crawlers
// see all city × specialty URLs without running JS.
//
// Size note: these 260 city × specialty links used to overflow the ~7680-byte
// URL cap React Router applies to its batched /__manifest discovery request,
// which silently discarded the whole batch — no route on the home page was ever
// discovered, so prefetch="viewport" emitted nothing here or in the footer
// (VIT-17787, revisiting the trade-off accepted in VIT-17716). Fixed by shipping
// the full manifest up front (routeDiscovery: 'initial', see
// react-router.config.ts), so there is no discovery URL left to overflow. SEO was
// never affected either way — every link is in the server HTML.
export const BrowseByCity = () => (
  // `id` is a stable `/#browse-by-city` deep-link target. The in-app links that
  // used it (UsOnlyNotice, the results outside-coverage state) were dropped in
  // VIT-17807, but the anchor is kept for external/shared links. `scroll-mt-4`
  // keeps the heading off the very top edge of the viewport after the jump.
  <section id="browse-by-city" className="scroll-mt-4 px-3 sm:px-4 py-4 bg-secondary-50">
    <div className="mx-auto max-w-7xl space-y-10 rounded-3xl bg-white py-10 sm:py-20 px-4 sm:px-10">
      <div className="mx-auto max-w-2xl space-y-4 text-center">
        <div className="mx-auto w-fit grid place-items-center border-2 border-primary-400 rounded-full py-4 sm:py-6 px-3">
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className="size-10 sm:size-12 stroke-primary-400"
          >
            <path
              d="M41 25C43.2091 25 45 23.2091 45 21C45 18.7909 43.2091 17 41 17C38.7909 17 37 18.7909 37 21C37 23.2091 38.7909 25 41 25Z"
              strokeWidth="2"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9 4C6.792 4 5 5.792 5 8V18C5 23.524 9.476 28 15 28C20.524 28 25 23.524 25 18V8C25 5.792 23.208 4 21 4"
              strokeWidth="2"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15 28V43C15 45.208 16.792 47 19 47C21.21 47 23 45.208 23 43V33C23 30.238 25.238 28 28 28C30.76 28 33 30.238 33 33.002V39C33 41.208 34.792 43 37 43C39.21 43 41 41.208 41 39V25"
              strokeWidth="2"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 4C12 7 9 7 9 7V1C9 1 12 1 12 4Z"
              strokeWidth="2"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M18 4C18 1 21 1 21 1V7C21 7 18 7 18 4Z"
              strokeWidth="2"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h2 className="font-display text-3xl text-secondary-700 sm:text-3xl">Browse doctors and dentists by city</h2>
        <p className="text-secondary-700">
          Find providers near you across major cities. Start with a location, then narrow by specialty and insurance.
        </p>
      </div>

      {/* One element per column, each holding a fixed slice — see CITY_COLUMNS
          for why this isn't CSS `columns`. `items-start` keeps an open city
          from stretching its siblings' height. */}
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-start gap-x-8 text-secondary-900 sm:grid-cols-3">
        {CITY_COLUMNS.map((column) => (
          <div key={column[0]?.label}>
            {column.map(({ label, city, region }) => {
              const location = formatLocationSlug(city, region)
              return (
                // `name` makes these an exclusive accordion — the browser closes
                // the previously open city, no state or JS required. It works
                // across the columns because the browser scopes `name` to the
                // whole document, not to a common parent.
                <details
                  key={location}
                  name="browse-by-city"
                  className="group details-reveal border-b border-secondary-200"
                  data-testid={`browse-city-${location}`}
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between py-3 font-medium marker:hidden [&::-webkit-details-marker]:hidden">
                    {label}
                    <ChevronDownIcon
                      aria-hidden="true"
                      className="size-4 shrink-0 text-secondary-500 transition-transform group-open:rotate-180"
                    />
                  </summary>
                  <ul className="list-none space-y-1 pb-3 pl-0">
                    {SPECIALTIES_BY_LABEL.map((specialty) => (
                      <li key={specialty.slug}>
                        <Link
                          to={href('/us/:location/:specialty', { location, specialty: specialty.slug })}
                          // Only the open city's links prefetch: a closed
                          // <details> gets `content-visibility: hidden`, and
                          // React Router's viewport observer treats that as not
                          // intersecting, so this costs 13 requests per city the
                          // user actually opens, not 260 on load. This only
                          // emits anything at all because the full manifest
                          // ships up front — without it the route doesn't match
                          // and PrefetchPageLinks renders nothing regardless of
                          // what the observer reports (VIT-17787).
                          prefetch="viewport"
                          viewTransition
                          className="block py-1 text-sm text-secondary-600 no-underline hover:text-primary-500"
                        >
                          {/* Plural, to match the destination page's h1 and <title>
                              ("71 Family Doctors in Austin, TX") — VIT-17926. */}
                          {specialty.pluralLabel} in {label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </details>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  </section>
)
